import { APP_NAME, APP_VERSION } from "#shared/constants.ts";

/**
 * Plain-text WhatsApp / bug-report payload (A4).
 * Same shape will feed future `POST /api/reports` — transport only later.
 */
export interface ProblemReportInput {
  errorId: string;
  code: string;
  message: string;
  /** Short label for what the user was doing, e.g. "save card". */
  action?: string;
  /** Display name or clerk handle when known. */
  userLabel?: string | null;
  /** Route pathname, optionally with a page title. */
  page?: string;
  occurredAt?: Date;
}

function buildStamp(): string {
  return typeof __CK_BUILD__ === "string" && __CK_BUILD__.length > 0 ? __CK_BUILD__ : "dev";
}

function browserLine(): string {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  let browser = "Browser";
  const chrome = /Chrome\/(\d+)/.exec(ua);
  const firefox = /Firefox\/(\d+)/.exec(ua);
  const safari = /Version\/(\d+).*Safari/.exec(ua);
  if (chrome) browser = `Chrome ${chrome[1]}`;
  else if (firefox) browser = `Firefox ${firefox[1]}`;
  else if (safari) browser = `Safari ${safari[1]}`;

  let os = "Unknown OS";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/Mac OS X/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/iPhone|iPad/i.test(ua)) os = "iOS";

  return `${browser} / ${os}`;
}

function formatWhen(d: Date): string {
  const local = d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const utc = d.toISOString().slice(0, 16).replace("T", " ");
  return `${local} (local) / ${utc} UTC`;
}

/** Build the readable plain-text block (not JSON). */
export function formatProblemReport(input: ProblemReportInput): string {
  const when = formatWhen(input.occurredAt ?? new Date());
  const page =
    input.page ??
    (typeof window !== "undefined" ? window.location.pathname : "/");
  const lines = [
    `${APP_NAME} problem report`,
    `Error: ${input.errorId}`,
    `Code: ${input.code}`,
    `When: ${when}`,
    `Page: ${page}`,
  ];
  if (input.action) lines.push(`Action: ${input.action}`);
  if (input.userLabel) lines.push(`User: ${input.userLabel}`);
  lines.push(`App: v${APP_VERSION} (build ${buildStamp()})`);
  lines.push(`Browser: ${browserLine()}`);
  lines.push(`Message: ${input.message}`);
  return lines.join("\n");
}

export async function copyProblemReport(input: ProblemReportInput): Promise<string> {
  const text = formatProblemReport(input);
  await navigator.clipboard.writeText(text);
  return text;
}
